/**
 * Utilitários para cálculo de saldo de títulos ERP (contas a pagar/receber).
 */

export const AMOUNT_TOLERANCE = 0.01;

export const SETTLEMENT_MODES = {
  PARTIAL: 'partial',
  FULL_WITH_ADJUSTMENT: 'full_with_adjustment',
  FULL_WITH_FEE_EXPENSE: 'full_with_fee_expense'
};

export function calculatePayableFinalAmount(row = {}) {
  const amount = parseFloat(row.amount || row.value || 0);
  const discount = parseFloat(row.discount_amount || row.discountAmount || 0);
  const interest = parseFloat(row.interest_amount || row.interestAmount || 0);
  const fine = parseFloat(row.fine_amount || row.fineAmount || 0);
  return amount - discount + interest + fine;
}

export function calculateReceivableFinalAmount(row = {}) {
  const amount = parseFloat(row.amount || row.value || 0);
  const discount = parseFloat(row.discount_amount || row.discountAmount || 0);
  const interest = parseFloat(row.interest_amount || row.interestAmount || 0);
  const fine = parseFloat(row.fine_amount || row.fineAmount || 0);
  return amount - discount + interest + fine;
}

export function calculateErpTitleFinalAmount(title = {}) {
  if (title.erpType === 'account_payable') {
    return calculatePayableFinalAmount(title);
  }
  if (title.erpType === 'account_receivable') {
    return calculateReceivableFinalAmount(title);
  }
  return parseFloat(title.value || title.amount || 0);
}

export function calculateErpTitlePaidAmount(title = {}) {
  return parseFloat(title.paidAmount ?? title.paid_amount ?? 0);
}

export function calculateErpTitleRemainingBalance(title = {}) {
  const finalAmount = calculateErpTitleFinalAmount(title);
  const paidAmount = calculateErpTitlePaidAmount(title);
  return Math.max(0, finalAmount - paidAmount);
}

export function isPartiallyPaidTitle(row = {}) {
  const paidAmount = parseFloat(row.paid_amount || row.paidAmount || 0);
  if (paidAmount <= AMOUNT_TOLERANCE) return false;
  if (row.status === 'paid') return false;
  return calculatePayableFinalAmount(row) - paidAmount > AMOUNT_TOLERANCE;
}

export function detectPartialReconciliation(transactionValue, title) {
  if (!title || (title.erpType !== 'account_payable' && title.erpType !== 'account_receivable')) {
    return null;
  }

  const normalizedValue = Math.abs(parseFloat(transactionValue || 0));
  const remainingBefore = calculateErpTitleRemainingBalance(title);

  if (normalizedValue + AMOUNT_TOLERANCE < remainingBefore) {
    return {
      transactionValue: normalizedValue,
      remainingBefore,
      remainingAfter: Math.max(0, remainingBefore - normalizedValue),
      adjustmentAmount: Math.max(0, remainingBefore - normalizedValue),
      title
    };
  }

  return null;
}

export function getApproximateMatchTolerance(remainingBalance) {
  return Math.max(Math.abs(remainingBalance) * 0.1, 50);
}

export function isApproximateValueMatch(transactionValue, title) {
  if (!title || (title.erpType !== 'account_payable' && title.erpType !== 'account_receivable')) {
    return false;
  }

  const normalizedValue = Math.abs(parseFloat(transactionValue || 0));
  const remainingBalance = calculateErpTitleRemainingBalance(title);

  if (remainingBalance <= AMOUNT_TOLERANCE) return false;

  const valueDiff = Math.abs(remainingBalance - normalizedValue);
  if (valueDiff <= AMOUNT_TOLERANCE) return false;

  return valueDiff <= getApproximateMatchTolerance(remainingBalance);
}

export function getPayableRemainingBalance(row = {}) {
  const finalAmount = calculatePayableFinalAmount(row);
  const paidAmount = parseFloat(row.paid_amount || 0);
  return Math.max(0, finalAmount - paidAmount);
}

export function enrichErpTitle(title) {
  const remainingBalance = calculateErpTitleRemainingBalance(title);
  return {
    ...title,
    remainingBalance,
    displayValue: remainingBalance
  };
}

export function formatTitleValueLabel(title, formatCurrency) {
  const remaining = calculateErpTitleRemainingBalance(title);
  const finalAmount = calculateErpTitleFinalAmount(title);
  const paidAmount = calculateErpTitlePaidAmount(title);

  if (paidAmount > AMOUNT_TOLERANCE && remaining > AMOUNT_TOLERANCE) {
    return `Saldo ${formatCurrency(remaining)} (original ${formatCurrency(finalAmount)})`;
  }

  return formatCurrency(finalAmount);
}
